import { ClearFilesEvent, SurveyModel } from 'survey-core';
import { deleteFile } from '../../api/files/delete-file';
import { patchSubmission } from '../../api/submissions/patch-submission';
import { UserType } from '../../types';

export interface DeleteFileHandlerProps {
  options: ClearFilesEvent;
  urlSlug: string;
  publicId?: string;
  surveyModel?: SurveyModel;
  isSigner?: boolean;
  trackingId?: string;
}

const buildDataAfterDeletion = (
  surveyData: Record<string, unknown>,
  questionName: string,
  currentValue: Array<{ name: string }>,
  fileNameToDelete: string | null,
) => {
  const remaining = fileNameToDelete
    ? currentValue.filter((f) => f.name !== fileNameToDelete)
    : [];

  return {
    ...surveyData,
    [questionName]: remaining,
  };
};

export const deleteFileHandler = async ({
  options,
  urlSlug,
  publicId,
  trackingId,
  surveyModel,
  isSigner = false,
}: DeleteFileHandlerProps) => {
  try {
    if (!options.value) {
      return options.callback('success');
    }

    const filesToDelete = options.fileName
      ? options.value.filter((file: { name: string }) => file.name === options.fileName)
      : options.value;

    if (!filesToDelete || filesToDelete.length === 0) {
      return options.callback('success');
    }

    const deletePromises = filesToDelete.map(async (file: { content: string }) => {
      const fileName = file.content.split('/').pop();
      if (!fileName) {
        throw new Error('Invalid file URL');
      }

      if (trackingId) {
        await deleteFile({
          urlSlug,
          trackingId,
          fileName,
        });
      } else if (publicId) {
        await deleteFile({
          urlSlug,
          publicId,
          fileName,
          isSigner,
        });
      } else {
        throw new Error('Either trackingId or publicId must be provided');
      }
    });

    await Promise.all(deletePromises);

    if (publicId && surveyModel) {
      const userType = isSigner ? UserType.SIGNER : UserType.INITIAL_USER;
      const updatedData = buildDataAfterDeletion(
        surveyModel.data,
        options.name,
        options.value,
        options.fileName,
      );
      await patchSubmission(urlSlug, publicId, updatedData, userType);
    }

    options.callback('success');
  } catch (error) {
    console.error('Error deleting files:', error);
    options.callback('error');
  }
};
